import React, { useState, useEffect } from 'react';
import { Row, Col, OverlayTrigger, Popover } from 'react-bootstrap';
import { Collapse } from 'reactstrap';
import { useLocation } from 'react-router';
import KaAlert from '../Common/KaAlert';
import useDisplayMessage from '../Common/useDisplayMessage';
import SpinnerButton from '../Common/SpinnerButton';
import BoldButton from '../Common/BoldButton';
import PaginatedTable from '../Common/Pagination/PaginatedTable';
import usePagination from '../Common/hooks/usePagination';
import useGetDataSharingConnections, { ConnectionStatus, DataSharingConnection, DataSharingSiteDto, Direction } from '../../requests/useGetDataSharingConnections';
import QueryView from '../Common/QueryView';
import { GenerateConnectionCodeModal } from './GenerateConnectionCodeModal';
import { RevokeConnectionModal } from './RevokeConnectionModal';


const renderConnectionInfo = (connection: DataSharingConnection) => {
  const popover = (
    <Popover id={`popover-${connection.id}`} style={{ maxWidth: '500px' }}>
      <Popover.Header as="h3">Connection Info</Popover.Header>
      <Popover.Body style={{ minWidth: '400px' }}>
        {connection.otherAdminEmail && (
          <div className="mb-2 text-nowrap">
            <strong>Partner Email:</strong> {connection.otherAdminEmail}
          </div>
        )}
        <div className="mb-2 text-nowrap">
          <strong>
            {connection.status === ConnectionStatus.Expired
              ? "Expired: "
              : connection.status === ConnectionStatus.Pending
                ? "Created: "
                : "Connected: "}
          </strong>
          {new Date(
            connection.status === ConnectionStatus.Expired && connection.expiresAt
              ? connection.expiresAt
              : connection.status === ConnectionStatus.Connected && connection.acceptedAt
                ? connection.acceptedAt
                : connection.createdAt
          ).toLocaleString()}
        </div>
        {connection.revokedAt && (
          <div className="mb-2 text-nowrap">
            <strong>Revoked:</strong> {new Date(connection.revokedAt).toLocaleString()}
          </div>
        )}
        {connection.declinedAt && (
          <div className="text-nowrap">
            <strong>Declined:</strong> {new Date(connection.declinedAt).toLocaleString()}
          </div>
        )}
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger={["hover", "focus"]}
      placement="left"
      overlay={popover}
    >
      <span className="text-primary ps-1" style={{ cursor: 'pointer' }}>
        <i className="bi bi-info-circle"></i> View Details
      </span>
    </OverlayTrigger>
  );
};

const StatusBadge = ({ connection }: { connection: DataSharingConnection }) => {
  let badgeClass = 'bg-secondary';

  switch (connection.status) {
    case ConnectionStatus.Connected:
      badgeClass = 'bg-success';
      break;
    case ConnectionStatus.Pending:
      badgeClass = 'bg-warning';
      break;
    case ConnectionStatus.Declined:
    case ConnectionStatus.Revoked:
    case ConnectionStatus.Expired:
      badgeClass = 'bg-danger';
      break;
    case ConnectionStatus.Multiple:
      badgeClass = 'bg-info';
      break;
  }

  return <span className={`badge ${badgeClass}`}>
    {connection.status}
  </span>;
};

const Mapping = ({ connection }: { connection: DataSharingConnection }) =>
  <p className="mb-0">
    {connection.direction === Direction.Source ? "Their Company" : 'Your Company' }
  </p>

const PartnerInfo = ({ connection }: { connection: DataSharingConnection }) => {
  const businessName = connection.otherBusinessName;
  const siteName = connection.otherSiteName;

  return businessName
    ? <div className="py-1" >
      <p className="mb-0 fw-semibold">{businessName}</p>
      {siteName && <p className="mb-0 text-muted small">{siteName}</p>}
    </div>
    : <span />;
};

const SingleConnectionRow = ({ site, onRevoke }: {
  site: DataSharingSiteDto;
  onRevoke: (connection: DataSharingConnection, siteName: string) => void
}) => {
  const connection = site.connections[0];

  return <Row className={`border-top`}>
    <Col xs={2} className="py-3">
      <p className="mb-0 fw-semibold">{site.siteName}</p>
    </Col>
    <Col xs={1} className="py-3 ms-n4">
      {site.connections.length === 0
        ? <span className='badge bg-secondary'>
          No Connections
        </span>
        : <StatusBadge connection={connection} />
      }
    </Col>
    <Col xs={2}>
      {site.connections.length === 0 ? '' : <PartnerInfo connection={connection} />}
    </Col>
    <Col xs={3} className="py-3">
      {site.connections.length === 0 ? '' : renderConnectionInfo(connection)}
    </Col>
    <Col xs={2} className="py-3">
      {site.connections.length === 0 ? '' : <Mapping connection={connection} />}
    </Col>
    <Col xs={2} className="text-end pt-2">
      {site.connections.length > 0 && connection.status === ConnectionStatus.Connected ? (
        <SpinnerButton
          className="btn-ghost-danger btn-sm"
          onClick={() => onRevoke(connection, site.siteName)}
          spinning={false}>
          Revoke
        </SpinnerButton>
      ) : ''}
    </Col>
  </Row>
};

const MultipleConnectionsRow = ({ siteData, isExpanded, onToggle, onRevoke }: {
  siteData: DataSharingSiteDto;
  isExpanded: boolean;
  onToggle: () => void;
  onRevoke: (connection: DataSharingConnection, siteName: string) => void;
}) => {
  const sortedConnections = [...siteData.connections].sort((a, b) => {
    if (a.status === ConnectionStatus.Connected && b.status !== ConnectionStatus.Connected) {
      return -1;
    }
    if (a.status !== ConnectionStatus.Connected && b.status === ConnectionStatus.Connected) {
      return 1;
    }

    const aBusinessName = a.otherBusinessName || '';
    const bBusinessName = b.otherBusinessName || '';

    return aBusinessName.localeCompare(bBusinessName);
  });

  const activeConnections = siteData.connections.filter(
    c => c.status === ConnectionStatus.Connected || c.status === ConnectionStatus.Pending
  );
  const showSingleActiveView = activeConnections.length <= 1;
  const primaryConnection = activeConnections[0];

  return <>
    <Row className="border-top align-items-center">
      <Col xs={2} className="py-3"><p className="mb-0 fw-semibold">{siteData.siteName}</p></Col>
      {showSingleActiveView ? (
        <>
          <Col xs={1} className="py-3 ms-n4">
            {primaryConnection ? <StatusBadge connection={primaryConnection} /> : <span className="badge bg-secondary">No Active</span>}
          </Col>
          <Col xs={2}>
            {primaryConnection ? <PartnerInfo connection={primaryConnection} /> : ''}
          </Col>
          <Col xs={3} className="py-3">
            {primaryConnection ? renderConnectionInfo(primaryConnection) : ''}
          </Col>
          <Col xs={2} className="py-3">
            {primaryConnection ? <Mapping connection={primaryConnection} /> : ''}
          </Col>
        </>
      ) : (
        <>
          <Col xs={1} className="py-3 ms-n4"><span className="badge bg-info">Multiple</span></Col>
          <Col xs={2} className="py-3"><p className="mb-0">{activeConnections.filter(c => c.status === ConnectionStatus.Connected).length} partners</p></Col>
          <Col xs={3} className="py-3"><span className="ps-5 ms-3">-</span></Col>
          <Col xs={2} className="py-3"><span className="ps-5 ms-4">-</span></Col>
        </>
      )}
      <Col xs={2} className="text-end py-1">
        {showSingleActiveView && primaryConnection?.status === ConnectionStatus.Connected ? (
          <SpinnerButton
            className="btn-ghost-danger btn-sm me-2"
            onClick={() => onRevoke(primaryConnection, siteData.siteName)}
            spinning={false}>
            Revoke
          </SpinnerButton>
        ) : ''}
        <button
          type="button"
          className="btn btn-icon btn-ghost-primary btn-link"
          data-testid={`showConnections-${siteData.siteName}`}
          onClick={onToggle}>
          <i
            data-testid={`expandIcon-${siteData.siteName}`}
            className={isExpanded ? 'bi-caret-up-square' : 'bi-caret-down-square'} />
        </button>
      </Col>
    </Row>
    <Collapse isOpen={isExpanded}>
      <div className="border-start border-end">
        {sortedConnections.map((connection) => (
          <Row key={connection.id} className="tree-row tree-row-centered tree-row-data-sharing">
            <Col xs={2} className="pb-3">
              <div className="mt-3">
                <StatusBadge connection={connection} />
              </div>
            </Col>
            <Col xs={1} className="ms-n4"></Col>
            <Col xs={2} className="pt-1"><PartnerInfo connection={connection} /></Col>
            <Col xs={3} className="pt-3">{renderConnectionInfo(connection)}</Col>
            <Col xs={2} className="pt-3"><Mapping connection={connection} /></Col>
            <Col xs={2} className="text-end pt-2">
              {connection.status === ConnectionStatus.Connected ? (
                <SpinnerButton
                  className="btn-ghost-danger btn-sm"
                  onClick={() => onRevoke(connection, siteData.siteName)}
                  spinning={false}
                >
                  Revoke
                </SpinnerButton>
              ) : ''}
            </Col>
          </Row>
        ))}
      </div>
    </Collapse>
  </>
};

const DataSharingContent = ({ sites, requery }: { sites: DataSharingSiteDto[], requery: () => void }) => {
  const location = useLocation();
  const displayMessage = useDisplayMessage();

  const pagination = usePagination(sites, 10);
  const [expandedSiteIds, setExpandedSiteIds] = useState<string[]>([]);
  const [showGenerateCodeModal, setShowGenerateCodeModal] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState<DataSharingConnection | undefined>();
  const [selectedSiteName, setSelectedSiteName] = useState<string | undefined>();

  useEffect(() => {
    if (location.state?.successMessage) {
      displayMessage.success(location.state.successMessage);
      window.history.replaceState({}, document.title);
    }
  }, []);

  const toggleSiteConnections = (siteId: string) => {
    const siteIndex = expandedSiteIds.indexOf(siteId);

    if (siteIndex === -1) {
      setExpandedSiteIds([...expandedSiteIds, siteId]);
    } else {
      const updatedIds = [...expandedSiteIds];
      updatedIds.splice(siteIndex, 1);
      setExpandedSiteIds(updatedIds);
    }
  };

  const handleRevokeConnection = (connection: DataSharingConnection, siteName: string) => {
    setSelectedConnection(connection);
    setSelectedSiteName(siteName);
  };

  const handleRevokeSuccess = () => {
    setSelectedConnection(undefined);
    setSelectedSiteName(undefined);
    displayMessage.success('Connection successfully revoked');
    requery();
  };

  return <div>
    <GenerateConnectionCodeModal
      sites={sites}
      show={showGenerateCodeModal}
      onClose={() => setShowGenerateCodeModal(false)}
      onSuccess={requery}
    />
    <RevokeConnectionModal
      show={!!selectedConnection}
      connection={selectedConnection}
      siteName={selectedSiteName}
      onClose={() => {
        setSelectedConnection(undefined);
        setSelectedSiteName(undefined);
      }}
      onSuccess={handleRevokeSuccess}
    />
    <Row className="justify-content-between mb-2">
      <h2 className="col-auto ka-blue">Manage Data Sharing</h2>
      <Col className="col-auto">
        <BoldButton 
          className="btn-ghost-primary"
          onClick={() => setShowGenerateCodeModal(true)}
        >
          Create Connection Code
        </BoldButton>
      </Col>
    </Row>
    <Row className="mb-3">
      <Col>
        <p>
          This page lets you view your access codes, connect with partners, and revoke access anytime. You stay in control while making collaboration simple.
        </p>
        <p>
          Sharing codes are single-use and expire after 24 hours. Generate a new code for each partner connection.
        </p>
        <p className="fw-bold">Sharing Options</p>
        <p>
          <ul>
            <li>
              <strong>One-Way (You → Partner):</strong> Partner can view your data. They handle mapping.
            </li>
            <li>
              <strong>One-Way (Partner → You):</strong> You can view partner data. You handle mapping.
            </li>
            <li>
              <strong>Two-Way:</strong> Both companies can view each other’s data. Each manages mapping on their side.
            </li>
          </ul>
        </p>
      </Col>
    </Row>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    <PaginatedTable
      onPageChange={pagination.setPage}
      currentPage={pagination.currentPage}
      pageSize={pagination.pageSize}
      totalPages={pagination.totalPages}>
      <div className="mt-3">
        <Row className="border-bottom border-dark border-1 pb-2">
          <Col xs={2}><strong>Site Name</strong></Col>
          <Col xs={1} className="ms-n4"><strong>Status</strong></Col>
          <Col xs={2}><strong>Partner Site</strong></Col>
          <Col xs={3}><strong>Connection Info</strong></Col>
          <Col xs={2}><strong>Mapping Responsibility</strong></Col>
          <Col xs={2} className="text-end"></Col>
        </Row>
        {pagination.items.map(siteData =>
          siteData.connections.length > 1
            ? <MultipleConnectionsRow
              key={siteData.siteId}
              siteData={siteData}
              isExpanded={expandedSiteIds.includes(siteData.siteId)}
              onToggle={() => toggleSiteConnections(siteData.siteId)}
              onRevoke={handleRevokeConnection} />
            : <SingleConnectionRow key={siteData.siteId} site={siteData} onRevoke={handleRevokeConnection} />
        )}
      </div>
    </PaginatedTable>
  </div>;
}

const DataSharingPage = () => {
  const getConnections = useGetDataSharingConnections();

  return <QueryView
    query={getConnections}
    renderData={siteConnections =>
      <DataSharingContent sites={siteConnections} requery={() => getConnections.query()} />} />
}

export { DataSharingPage };